import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { buildBrandedCloudinaryImageTransforms } from '@/layout/Seo/SeoHelper'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getOffersForProject, Offer } from '@/services/PifService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PayItForwardExpressCheckoutView } from '@/views/PayItForward/PayItForwardExpressCheckoutView'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  locale: string
  offers: Offer[]
  projectMeta: ProjectMeta
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  projectMeta,
}) => {
  const { t } = useTranslate('pif')

  const projectName = projectMeta.name
  const optionalDescription = t(
    'payItForwardForProjectDescriptionv2',
    'Pay it Forward for {{projectName}} on Angel and help support stories you love, enabling others to experience them too',
    {
      projectName,
    },
  )

  return (
    <div className="bg-core-gray-100">
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath:
            projectMeta.slug === slugs.david
              ? 'v1761851105/david/open-graph-david-251030b.jpg'
              : projectMeta.discoveryPosterLandscapeCloudinaryPath,
          cloudinaryTransformation: cloudinaryImageTransformation,
          description:
            projectMeta.slug === slugs.david
              ? t(
                  'pifDavidSeoDescription',
                  'Join the movement to send 1 million kids to see DAVID: The Animated Movie in theaters for free. Pay it Forward and share a story of faith and courage.',
                )
              : projectMeta.pifExplainerText || optionalDescription,
          locale,
          path: `${paths.payItForward.index}/${projectMeta.slug}`,
          title:
            projectMeta.slug === slugs.david
              ? t('pifDavidSeoTitle', 'Help 1 Million Kids See DAVID in Theaters This Christmas')
              : t('payItForwardForProjectAngel', 'Pay it Forward for {{projectName}} on Angel', { projectName }),
        })}
      />
      <ProjectMetaProvider project={projectMeta}>
        <MarketingVideoPopup />
        <PayItForwardExpressCheckoutView project={projectMeta} offers={offers} />
      </ProjectMetaProvider>
    </div>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, params, preview }) => {
    const slug = params?.projectSlug as string

    const projectMeta = await getProjectMeta({ slug }, federationClient, { locale, preview })
    const offers = await getOffersForProject({ projectSlug: slug }, federationClient)

    if (!offers?.length || !projectMeta || !projectMeta.pifEnabled) {
      return {
        redirect: {
          destination: paths.payItForward.forYourConsideration,
          permanent: false,
        },
      }
    }

    // TODO Remove once Truth and Treason Movie is out of theaters
    if (slug === slugs.truthAndTreasonSeries) {
      return {
        redirect: {
          destination: paths.payItForward.truthAndTreason,
          permanent: false,
        },
      }
    }

    // TODO Remove once SOF is out of theaters
    const cloudinaryImageTransformation = buildBrandedCloudinaryImageTransforms({
      logoCloudinaryPath: projectMeta.logoCloudinaryPath,
    })

    return {
      props: {
        cloudinaryImageTransformation,
        locale,
        offers,
        projectMeta,
        ...(await loadTranslations(locale, ['common', 'pif', 'account'])),
      },
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export default PayItForwardProjectCheckoutPage
